import {Routes} from '@angular/router';
import {AppRootContainerComponent} from './app-layout/app-root-container/app-root-container.component';
import {ProfileRootComponent} from './profiles/profile-root/profile-root.component';
import {ProfileGuard} from './profiles/profile.guard';
import {NoProfileAvailableComponent} from './app-layout/no-profile-available/no-profile-available.component';
import {AppWelcomeComponent} from './app-layout/app-welcome/app-welcome.component';
import {ErrorComponent} from '@shared/error-handling/error/error.component';
import {AttributionPreloadGuard} from './admin/attributions/attribution-preload.guard';
import {AccessRight} from '@model/access-right';
import {StaffMemberAccessRightRouteGuard} from './app-layout/route-guards/staff-member-accessright-route-guard';
import {PageNotFoundComponent} from '@shared/page-not-found/page-not-found.component';
import {ParentAccessRightGuard} from './app-layout/route-guards/parent-access-right-guard.service';
import {StudentAccessRightGuard} from './app-layout/route-guards/student-access-right-guard.service';
import {AppAboutComponent} from './app-layout/app-about/app-about.component';
import {StaffmemberConsultFacadeService} from './admin/consult/staffmember-consult-facade.service';
import {SchoolReportFacadeService} from './admin/school-report/school-report-facade.service';
import {PlanningFacadeService} from './admin/planning/planning-facade.service';
import {CollaborativeWorkFacadeService} from './admin/collaborative-work/collaborative-work-facade.service';
import {ScoreBookFacadeService} from './admin/score-book/score-book-facade.service';
import {EvaluationPreloadGuard} from './admin/score-book/evaluation-preload.guard';
import {CommunicationFacadeService} from './admin/communication/communication-facade.service';
import {StaffMemberAbsenceFacadeService} from './admin/staff-member-absence/staff-member-absence-facade.service';
import {StatisticsFacadeService} from './admin/statistics/statistics-facade.service';
import {CertificationsPerUnitFacadeService} from './admin/certification-per-unit/certification-per-unit-facade.service';
import {ResourcesFacadeService} from './admin/resources/resources-facade.service';
import {PedagogicalFollowUpFacadeService} from './admin/pedagogical-follow-up/pedagogical-follow-up-facade.service';
import {ParentMeetingFacadeService} from './admin/parent-meeting/parent-meeting-facade.service';
import {ParentPreloadGuard} from './parent/parent-preload.guard';
import {ParentFacadeService} from './parent/parent-facade.service';
import {StudentFacadeService} from './student/student-facade.service';
import {ValveFacadeService} from './valve/valve-facade.service';
import {ManagementFacadeService} from './admin/management/management-facade.service';
import {SuperAdminFacadeService} from './super-admin/schools/super-admin-facade-service';
import {AttributionFacadeService} from './admin/attributions/attribution-facade.service';
import {StudentsManagementFacadeService} from './admin/students/students-management-facade.service';
import {DisciplinaryFacadeService} from './admin/disciplinary/disciplinary-facade.service';

export const appRoutes: Routes = [
  {
    path: '', redirectTo: 'app', pathMatch: 'full'
  },
  {
    path: 'app', component: AppRootContainerComponent,
    canActivate: [ProfileGuard],
    children: [
      {
        path: '', pathMatch: 'full',
        component: AppWelcomeComponent
      },
      {
        path: 'about',
        component: AppAboutComponent
      },
      {
        path: 'super-admin',
        providers: [SuperAdminFacadeService],
        loadChildren: () => import('./super-admin/super-admin.routes').then(routes => routes.superAdminRoutes)
      },
      {
        path: 'admin',
        children: [
          {
            path: 'management',
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.FIRST_ADMIN, AccessRight.ADMIN]
            },
            providers: [ManagementFacadeService],
            loadChildren: () => import('./admin/management/management.routes').then(routes => routes.managementRoutes)
          },
          {
            path: 'attributions',
            canActivate: [AttributionPreloadGuard, StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.FIRST_ADMIN, AccessRight.ADMIN]
            },
            providers: [AttributionPreloadGuard, AttributionFacadeService],
            loadChildren: () => import('./admin/attributions/attributions.routes').then(routes => routes.attributionsRoutes)
          },
          {
            path: 'students',
            providers: [StudentsManagementFacadeService],
            loadChildren: () => import('./admin/students/students-management.routes').then(routes => routes.studentsRoutes)
          },
          {
            path: 'consult',
            providers: [StaffmemberConsultFacadeService],
            loadChildren: () => import('./admin/consult/consult.routes').then(routes => routes.consultRoutes)
          },
          {
            path: 'school-report',
            providers: [SchoolReportFacadeService],
            loadChildren: () => import('./admin/school-report/school-report.routes').then(routes => routes.schoolReportRoutes)
          },
          {
            path: 'planning',
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.TEACHER]
            },
            providers: [PlanningFacadeService],
            loadChildren: () => import('./admin/planning/planning.routes').then(routes => routes.planningRoutes)
          },
          {
            path: 'collaborative-work',
            providers: [CollaborativeWorkFacadeService],
            loadChildren: () => import('./admin/collaborative-work/collaborative-work.routes').then(routes => routes.collaborativeWorkRoutes)
          },
          {
            path: 'score-book',
            providers: [ScoreBookFacadeService, EvaluationPreloadGuard],
            loadChildren: () => import('./admin/score-book/score-book.routes').then(routes => routes.scoreBookRoutes)
          },
          {
            path: 'communication',
            providers: [CommunicationFacadeService],
            loadChildren: () => import('./admin/communication/communication.routes').then(routes => routes.communicationRoutes)
          },
          {
            path: 'staffMemberAbsence',
            providers: [StaffMemberAbsenceFacadeService],
            loadChildren: () => import('./admin/staff-member-absence/staff-member-absence.routes').then(routes => routes.staffMemberAbsenceRoutes)
          },
          {
            path: 'settings',
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.FIRST_ADMIN, AccessRight.ADMIN, AccessRight.TEACHER]
            },
            loadChildren: () => import('./admin/settings/settings.routes').then(routes => routes.settingsRoutes)
          },
          {
            path: 'statistics',
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.FIRST_ADMIN, AccessRight.ADMIN, AccessRight.EDUCATOR]
            },
            providers: [StatisticsFacadeService],
            loadChildren: () => import('./admin/statistics/statistics.routes').then(routes => routes.statisticsRoutes)
          },
          {
            path: 'certifications-per-unit',
            providers: [CertificationsPerUnitFacadeService],
            loadChildren: () => import('./admin/certification-per-unit/certification-per-unit.routes').then(routes => routes.certificationPerUnitRoutes)
          },
          {
            path: 'resources',
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.RESOURCE_MANAGEMENT]
            },
            providers: [ResourcesFacadeService],
            loadChildren: () => import('./admin/resources/resources.routes').then(routes => routes.resourcesRoutes)
          },
          {
            path: 'ilp',
            providers: [PedagogicalFollowUpFacadeService],
            loadChildren: () => import('./admin/pedagogical-follow-up/pedagogical-follow-up.routes').then(routes => routes.pedagogicalFollowUpRoutes)
          },
          {
            path: 'parent-meeting',
            providers: [ParentMeetingFacadeService],
            loadChildren: () => import('./admin/parent-meeting/parent-meeting.routes').then(routes => routes.parentMeetingRoutes)
          },
          {
            path: 'disciplinary',
            providers: [DisciplinaryFacadeService, StudentsManagementFacadeService],
            canActivate: [StaffMemberAccessRightRouteGuard],
            data: {
              accessRight: [AccessRight.ILP_MANAGEMENT, AccessRight.FIRST_ADMIN, AccessRight.ADMIN, AccessRight.EDUCATOR, AccessRight.TEACHER]
            },
            loadChildren: () => import('./admin/disciplinary/disciplinary.routes').then(routes => routes.discplinaryRoutes)
          },
        ],
      },
      {
        path: 'parent',
        canActivate: [ParentAccessRightGuard],
        providers: [ParentPreloadGuard, ParentFacadeService],
        loadChildren: () => import('./parent/parent.routes').then(routes => routes.parentRoutes)
      },
      {
        path: 'student',
        canActivate: [StudentAccessRightGuard],
        providers: [StudentFacadeService],
        loadChildren: () => import('./student/student.routes').then(routes => routes.studentRoutes)
      }
    ]
  },
  {
    path: 'valve',
    canActivate: [ProfileGuard],
    providers: [ValveFacadeService],
    loadChildren: () => import('./valve/valve.routes').then(routes => routes.valvesRoutes)
  },
  {
    path: 'profiles',
    component: ProfileRootComponent
  },
  {
    path: 'noprofiles',
    component: NoProfileAvailableComponent
  },
  {
    path: 'error',
    component: ErrorComponent
  },
  {
    path: '**',
    component: PageNotFoundComponent
  }
];
